import React from "react";

/**
 * UrduInput — renders an Urdu companion input/textarea beneath any English field.
 *
 * Props:
 *  label       — Urdu label text (e.g. "نام (اردو)")
 *  name        — HTML name attribute
 *  value       — controlled value
 *  onChange    — change handler
 *  placeholder — optional RTL placeholder
 *  type        — "input" (default) or "textarea"
 *  rows        — rows for textarea (default 3)
 *  required    — whether required
 */
export function UrduInput({
  label,
  name,
  value,
  onChange,
  placeholder,
  type = "input",
  rows = 3,
  required = false,
  className = "",
  error,
  register,
}) {
  const baseClass = "ui-input-field text-right";
  const borderClass = error
    ? "border-brand-error hover:border-brand-error focus:border-brand-error"
    : "";
  const registered = register && name ? register(name) : {};

  return (
    <div className={className || "mt-2"}>
      {label && (
        <label
          className="ui-form-label text-right"
          dir="rtl"
        >
          {label}
          {required ? <span className="text-red-500"> *</span> : null}
        </label>
      )}
      {type === "textarea" ? (
        <textarea
          name={name}
          value={register ? undefined : value || ""}
          onChange={register ? undefined : onChange}
          placeholder={placeholder || "اردو میں لکھیں..."}
          dir="rtl"
          rows={rows}
          required={required}
          className={`ui-textarea-field text-right ${borderClass} min-h-[88px] h-[88px]`}
          {...registered}
        />
      ) : (
        <input
          type="text"
          name={name}
          value={register ? undefined : value || ""}
          onChange={register ? undefined : onChange}
          placeholder={placeholder || "اردو میں لکھیں..."}
          dir="rtl"
          required={required}
          className={`${baseClass} ${borderClass}`}
          {...registered}
        />
      )}
      {error ? <p className="ui-form-error text-right">{error}</p> : null}
    </div>
  );
}

/**
 * UrduFieldPair — wraps an English field and its Urdu companion in a visually grouped card.
 *
 * Usage:
 *   <UrduFieldPair>
 *     <EnglishInput ... />
 *     <UrduInput ... />
 *   </UrduFieldPair>
 */
export function UrduFieldPair({ children, className = "" }) {
  return (
    <div className={`rounded-xl border border-gray-300 p-3 bg-gray-50 space-y-1 ${className}`}>
      {children}
    </div>
  );
}

export default UrduInput;
